<template>
  <input-widget :model="model"
                :attribute="attribute"
                type="treepicker"
                :disabled="isSelectDisabled"
                display-field="${item.name}"
                :hint="hint"
                :label="label"
                :title-hint="titleHint"
                :placeholder="computedPlaceholder"
                :tree-picker-options="treePickerOptions"
                :items="items"
                :multiple="isMultiple"
                @select2Unselect="onUnselect"
                @onTreepickerSelect="onTreepickerSelect"
  />
</template>


<script>
  import InputWidget from "./../components/input-widget/InputWidget";
  import {createNamespacedHelpers} from "vuex";
  import {Formatter} from "@/core/services/formatter.service";

  const {mapState: mapStateDepartment} = createNamespacedHelpers('setup/department');

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "MultipleCategorySelectInputWidget",
    components: {InputWidget},
    data() {
      return {
        items: []
      }
    },
    props: {
      model: {
        type: Object,
        required: true
      },
      attribute: {
        type: String,
        required: true
      },
      hint: {
        type: [Boolean, String],
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      multiple: {
        type: Boolean,
        default: true
      },
      titleHint: {
        type: [Boolean, String],
        default: false
      },
      placeholder: {
        type: [String, Boolean],
        default: null
      },
      flat: {
        type: Boolean,
        default: true
      },
      label: {
        type: [Boolean, String],
        default: function () {
          return this.attribute
        }
      },
      options: {
        type: Object,
        default: () => {
          return {};
        }
      },
      onUnselect: {
        type: Function,
        default: () => {
        }
      },
      /** When true, only categories under selectedDepartmentUuids are listed (e.g. Buyer Purchase Threshold Report). */
      limitBySelectedDepartments: {
        type: Boolean,
        default: false
      },
      selectedDepartmentUuids: {
        type: Array,
        default: () => []
      }
    },
    computed: {
      ...mapStateDepartment(['departments']),
      isSelectDisabled() {
        if (this.disabled) {
          return true;
        }
        if (this.limitBySelectedDepartments &&
          (!this.selectedDepartmentUuids || this.selectedDepartmentUuids.length === 0)) {
          return true;
        }
        return false;
      },
      isMultiple() {
        if (this.options && Object.prototype.hasOwnProperty.call(this.options, 'multiple')) {
          return this.options.multiple !== false;
        }
        return this.multiple;
      },
      computedPlaceholder() {
        if (this.placeholder) {
          return this.placeholder;
        }
        if (this.options && this.options.placeholder) {
          return this.options.placeholder;
        }
        return this.$i18n.t('Select Categories');
      },
      treePickerOptions() {
        return {
          normalizer: (node) => {
            return {
              id: node.uuid,
              label: Formatter.getByMasterLanguage(node.name),
              children: node.children,
            }
          },
          flat: this.flat,
          valueConsistsOf: 'ALL',
        }
      }
    },
    methods: {
      addCategoryUuidsFromTree(nodes, outSet) {
        if (!nodes || !nodes.length) {
          return;
        }
        for (let i = 0; i < nodes.length; i++) {
          const n = nodes[i];
          if (n.uuid) {
            outSet.add(n.uuid);
          }
          if (n.children && n.children.length) {
            this.addCategoryUuidsFromTree(n.children, outSet);
          }
        }
      },
      pruneCategorySelectionToVisibleTree() {
        if (!this.limitBySelectedDepartments) {
          return;
        }
        const valid = new Set();
        this.addCategoryUuidsFromTree(this.items, valid);
        const cur = this.model[this.attribute];
        if (!cur || !cur.length) {
          return;
        }
        const filtered = cur.filter((id) => valid.has(id));
        if (filtered.length !== cur.length) {
          this.$set(this.model, this.attribute, filtered);
        }
      },
      clearCategorySelectionIfDepartmentScopeChanged() {
      },
      initCategories() {
        this.items = [];
        if (!this.departments || !this.departments.rows) {
          return;
        }
        let departmentsToWalk = this.departments.rows;
        if (this.limitBySelectedDepartments) {
          const selected = new Set(this.selectedDepartmentUuids || []);
          if (selected.size === 0) {
            this.$set(this.model, this.attribute, []);
            return;
          }
          departmentsToWalk = departmentsToWalk.filter(d => selected.has(d.uuid));
        }
        for (let department of departmentsToWalk) {
          if (department.categories) {
            this.items = [...this.items, ...department.categories];
          }
        }
        this.pruneCategorySelectionToVisibleTree();
      },
      onTreepickerSelect(val) {
        this.$emit('select', val);
      },
    },
    watch: {
      departments: {
        deep: true,
        handler() {
          this.initCategories();
        }
      },
      selectedDepartmentUuids: {
        deep: true,
        handler() {
          this.initCategories();
        }
      },
      limitBySelectedDepartments() {
        this.initCategories();
      }
    },
    mounted() {
      this.initCategories();
    }
  }
</script>

<style scoped>

</style>
